import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {DateString} from "./Projects";
import {TID} from "../misc/lists/ListCommon";
import {PrepDetail, PrepFromGroup} from "./QuoteHardware";

// "none" leaves the current by-others flag untouched; the others set it explicitly.
export type ByOthersSetting = "none" | "byUs" | "byOthers" | "inherit";

export class Openings extends SubAPI {
    list(input: {
        project: number
        type?: "for-release" | "have-no-hardware-group" | "add-hw-find-opening-number" | "for-hardware-group";
        category?: "frame" | "door" | "hardware";
        offset?: number;
        search?: string;
        previewChangeset?: number;
        hwGroup?: number;
    }) {
        return this.fetcher.get<Paginated<Opening>>("/api/openings", input)
    }

    getByIds(input: {
        project: number;
        ids: number[]
        previewChangeset?: number;
    }) {
        return this.fetcher.post<Opening[]>("/api/openings/by-id", input)
    }

    listScreenElevations(input: {
        project: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<ScreenElevation>>("/api/opening/screen-elevations", input)
    }

    updateScreenElevation(input: ScreenElevation) {
        return this.fetcher.post<{}>("/api/opening/screen-elevation", input)
    }

    listDrawingWallLabels(input: {
        project: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<DrawingWallLabel>>("/api/opening/drawing-wall-labels", input)
    }

    updateDrawingWallLabel(props: DrawingWallLabel) {
        return this.fetcher.post<{}>("/api/opening/drawing-wall-label", props)
    }

    listDoorElevations(input: {
        project: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<DoorElevation>>("/api/opening/door-elevations", input)
    }

    updateDoorElevation(input: DoorElevation) {
        return this.fetcher.post<{}>("/api/opening/door-elevation", input)
    }

    upsert(input: (Opening | {project: number}) & {userApprovedMultiEdit?: boolean; identityResolution?: "update-all" | "detach"}) {
        return this.fetcher.post<TID>("/api/opening", input)
    }

    bulkUpsert(input: (Opening | {project: number})[]) {
        return this.fetcher.post<{ids: number[]}>("/api/opening/bulk", input)
    }

    reSequence(input: {
        project: number
        idOrder: number[]
    }) {
        return this.fetcher.post<{}>("/api/opening/re-sequence", input)
    }

    reOrder(input: {
        project: number;
        idOrder: number[]
    }) {
        return this.fetcher.post<{}>("/api/opening/re-order", input)
    }

    resetConflict(input: {
        project: number;
        opening: number;
    }) {
        return this.fetcher.post<{}>("/api/opening/reset-conflict", input)
    }

    recalculate(input: {
        project: number;
        openings: number[];
        keepInReview?: boolean;
    }) {
        return this.fetcher.post<{}>("/api/opening/recalculate", input)
    }

    release(input: {
        note: string;
        project: number;
        neededBy: null | DateString;

        doorForOpenings?: number[];
        frameForOpenings?: number[];

        type?: string;
        requests?: number[];

        unRelease: boolean;
        confirmUnreleasePODraft?: boolean;
        includeRelatedAnchors?: boolean;
    }) {
        return this.fetcher.post<ReleaseResult>("/api/release", input)
    }

    releaseTask(input: {
        note: string;
        project: number;
        neededBy: null | DateString;

        doorForOpenings?: number[];
        frameForOpenings?: number[];

        type?: string;
        requests?: number[];

        unRelease: boolean;
        confirmUnreleasePODraft?: boolean;
        includeRelatedAnchors?: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/release/task", input)
    }

    listFrameProfiles(props: {
        project: number;
    }) {
        return this.fetcher.get<FrameProfile[]>("/api/opening/frame-profiles", props)
    }

    listAnchors(props: {
        project: number;
    }) {
        return this.fetcher.get<AnchorDetail[]>("/api/opening/anchors", props)
    }

    listWoodDetails(props: {
        project: number;
    }) {
        return this.fetcher.get<WoodDetailItem[]>("/api/opening/wood-detail", props);
    }

    updateWoodDetail(input: WoodDetailItem) {
        return this.fetcher.post("/api/opening/wood-detail", input);
    }

    listDoorCores(props: {
        project: number;
    }) {
        return this.fetcher.get<DoorCore[]>("/api/opening/door-cores", props);
    }

    upsertDoorCore(input: DoorCore) {
        return this.fetcher.post("/api/opening/door-core", input);
    }

    listDoorSeams(props: {
        project: number;
    }) {
        return this.fetcher.get<DoorSeam[]>("/api/opening/door-seams", props);
    }

    upsertDoorSeam(input: DoorSeam) {
        return this.fetcher.post("/api/opening/door-seam", input);
    }
}

export type ReleaseResult = TID & {
    confirmUnreleasePODraft?: string;
    hasNonStockingAvailable: boolean;
    confirmIncludeRelatedAnchors: boolean;
    releaseId: number;
}

export type DoorCore = {
    id: number; // dummy
    doreCore: string;
    doorCoreFile: number | null;
    doorCoreFileName: string | null;
    count: number;
    quoteAlternative?: number;
    project: number;
}

export type DoorSeam = {
    id: number; // dummy
    doreSeam: string;
    doorSeamFile: number | null;
    doorSeamFileName: string | null;
    count: number;
    quoteAlternative?: number;
    project: number;
}

export type WoodDetailItem = {
    id: number; // dummy
    doorMaterial: string;
    prevDoorWdMaterialName: string;
    doorWdMaterialName: string;
    doorWdSpecies: string;
    doorWdCut: string;
    doorWdMatching: string;
    doorWdMatchingMethod: string;
    doorWdPly: string;
    doorFinish: string;

    doorWdSwatchColor: string;
    doorWdProofFile?: number | null;
    doorWdProofFileName?: string | null;

    count: number;
    quoteAlternative?: number;
    project: number;
}

export type ScreenElevation = {
    id: number;
    name: string;
    file?: number;
    fileName?: string;
    project: number;

    screenElevation: string;
    screenElevationFile?: number;
    screenElevationFileName?: string;
    screenElevationOutsideHeight: string;
    screenElevationOutsideWidth: string;
    hasFixedMullion: boolean;
    screenElevationRenameOnlyFlag?: boolean;

    width?: string;
    height?: string;
    count: number;
}

export type FrameProfile = {
    id: number;

    frameProfile: string;
    frameProfileFile?: number;
    frameProfileFileName?: string;

    count: number;
}

export type AnchorDetail = {
    id: number;

    frameAnchorProduct: number;
    frameAnchorProductName: string;
    frameAnchorProductCatalog?: number;

    count: number;
}

export type DoorElevation = {
    id: number;
    name: string;
    file?: number;
    fileName?: string;
    project: number;

    doorElevation: string;
    doorElevationFile?: number;
    doorElevationFileName?: string;
    doorElevationRenameOnlyFlag?: boolean;

    doorElevationProfileDefaultName?: string;
    doorElevationProfileDefaultFile?: number;
    doorElevationProfileDefaultFileName?: string;
    doorElevationProfileCustomFile?: number;
    doorElevationProfileCustomFileName?: string;

    prevName: string;
    count: number;
}

export type DrawingWallLabel = {
    id: number;
    name: string;
    file?: number;
    fileName?: string;

    drawingWallLabel: string;
    drawingWallLabelFile?: number;
    drawingWallLabelFileName?: string;
    drawingWallLabelRenameOnlyFlag?: boolean;

    project: number;

    jambDepth: string;
    frameConstruction: string;
    frameProfileIsFF: boolean;
    frameAnchorProduct?: number;
    frameAnchorProductName?: string;

    count: number;
}

export type Opening = {
    id: number;
    name: string;
    project: number;
    seqNumber: number;
    qty: number;

    hardwareGroup?: number;
    hardwareGroupName?: string;
    hardwareGroupPrepConfig?: PrepDetail[];
    hardwareGroupPrepConfigIsCustom: boolean;
    prepFromGroup?: PrepFromGroup;
    prepIsCustom?: boolean;
    hwDescriptiveName?: string;
    hardwareGroupShortcodeName?: string;
    hardwareGroupShortcode?: number;

    frameRelease?: number;
    doorRelease?: number;

    locationOne: string;
    locationTransition: "to"|"from"|"";
    locationTwo: string;
    floor: string;
    phase: string;
    exterior: boolean;
    openingType: string;
    nominalWidth: string;
    inactiveDoorWidth: string;
    nominalHeight: string;
    handing: string;
    flipDoubleActiveHanding: boolean;
    label: string;
    frameSeries: string;
    frameMaterial: string;
    frameGauge: string;

    screenElevation: string;
    screenElevationFileName?: string;
    screenElevationFile?: number;
    screenElevationOutsideWidth: string;
    screenElevationOutsideHeight: string;
    hasFixedMullion: boolean;

    // the new drw-based elevation links. When drwScreenElevation is set the opening uses
    // the new elevation tool for the screen (frame); otherwise it falls back to the legacy
    // file-based screenElevation above. drwScreenElevationName is the resolved screen name.
    drwScreenElevation?: number;
    drwScreenElevationName?: string;
    // the linked screen rendering's measurements (drw openings only; legacy openings use the
    // screenElevationOutside* fields).
    drwScreenRenderingMeta?: DrwRenderingMeta | null;
    drwScreenElevationChanged?: boolean;
    // multi-opening screens: on a secondary opening, the opening that owns the frame and the
    // door slot (by name) this opening fills on the primary's screen. Null on a primary.
    drwScreenPrimaryOpening?: number | null;
    drwScreenPrimaryOpeningName?: string | null;
    drwScreenSlot?: string | null;
    drwDoorElevation?: number;
    drwDoorElevationName?: string;
    // true when a drawn lite of the drw door elevation has thermal glass. Read-only.
    drwDoorThermal?: boolean;
    drwDoorElevationChanged?: boolean;
    drwHardwareElevation?: number;
    drwHardwareElevationName?: string;
    drwHardwareElevationChanged?: boolean;

    // submittal rendering file ids, attached per opening by the elevation tool's
    // render-on-save. Printouts read these and build api.files.downloadLink(id).
    drwScreenRendering?: number;
    drwDoorRendering?: number;
    drwHardwareRendering?: number;

    // the render group's label (A, B, C…) attached alongside each rendering, printed on the
    // image as "{elevName}-{label}".
    drwScreenElevationLabel?: string;
    drwDoorElevationLabel?: string;
    drwHardwareElevationLabel?: string;

    frameProfile: string;
    frameProfileFile: number | null;
    frameProfileFileName: string | null;

    jambDepth: string;

    drawingWallLabel: string;
    drawingWallLabelFile?: number;
    drawingWallLabelFileName?: string;

    frameConstruction: string;
    frameHardwarePreps: string;
    doorSeries: string;
    doorMaterial: string;

    doorWdMaterialName: string;
    doorWdSpecies: string;
    doorWdCut: string;
    doorWdMatching: string;
    doorWdMatchingMethod: string;
    doorWdPly: string;
    doorWdSwatchName: string;
    doorWdProofFile: number | null;

    doorGauge: string;
    doorThickness: string;

    doorElevation: string;
    doorElevationFile?: number;
    doorElevationFileName?: string;
    inactiveDoorElevation: string;
    inactiveDoorElevationFile?: number;
    inactiveDoorElevationFileName?: string;

    // elevations from the door/frame products on this opening's pending inventory
    // requests. Only filled for type=for-release lists.
    releaseDoorElevationCalc: string;
    releaseInactiveDoorElevationCalc: string;
    releaseScreenElevationCalc: string;

    doorLiteKitActive?: number;
    doorLiteKitActiveFile?: number;
    doorLiteKitInactiveFile?: number;

    doorCore: string;
    doorSeam: string;
    doorFinish: string;
    notes: string;

    // null = inherit by-others from the series' supplier category; non-null = explicit override
    doorActiveByOthers: boolean | null;
    doorInactiveByOthers: boolean | null;
    frameByOthers: boolean | null;

    // effective by-others computed by the backend: the explicit override above when set,
    // otherwise inherited from the series' supplier category. Read-only.
    doorActiveByOthersCalc: boolean;
    doorInactiveByOthersCalc: boolean;
    frameByOthersCalc: boolean;

    frameAnchorProduct?: number;
    frameAnchorProductName?: string;
    frameAnchorQtyOverride: number | null;
    // the linked screen elevation's anchors: whether it has any, and their totals from its ready
    // render ("P&S x4, Z x2"; null until the render is ready). When it has any, they replace the
    // anchor columns above.
    drwHasAnchors: boolean;
    drwAnchorSummary: string | null;
    // the products of those anchors; empty until the render is ready.
    drwAnchorProducts: number[] | null;

    sortOrder: number;

    frameStatus: string;
    doorStatus: string;

    createdAt: DateString;
    updatedAt: DateString;
    archived: boolean;

    historyCount: number;

    backup: Opening | null;
    quotedOpening?: number | null;

    degreesOfOpeningActive: number | null;
    degreesOfOpeningInactive: number | null;

    logictrl: OpeningLogiCtrlStatus | null;
}

export type OpeningLogiCtrlStatus = {
    frameEdited: boolean;
    frameError: string | null;
    frameSkipRules: boolean;
    anchorsEdited: boolean;
    anchorsError: string | null;
    anchorsSkipRules: boolean;
    doorEdited: boolean;
    doorError: string | null;
    doorSkipRules: boolean;
    hardwareEdited: boolean;
    hardwareError: string | null;
    hardwareSkipRules: boolean;
    conflictsWithMain: boolean;
    glazing: OpeningLogiCtrlGlazing[];
}

// one of the opening's glazing rows, with its own logistics status
export type OpeningLogiCtrlGlazing = {
    id: number;
    marks: string;
    edited: boolean;
    error: string | null;
    skipRules: boolean;
}

// measurements the elevation render reports for a screen rendering, in the project's units.
export type DrwRenderingMeta = {
    outsideWidth: string;
    outsideHeight: string;
}
